/**
 * Centers .product-gallery__hover-arrow on the visible product card image
 * (not full card height) for product-list carousels in .product-gallery__carousel-wrapper.
 */
const LIST_CAROUSEL_SUBSTR = 'product-list--carousel';

const bindings = new WeakMap();

function listIsCarousel(list) {
  return list?.tagName === 'PRODUCT-LIST' && [...list.classList].some((c) => c.includes(LIST_CAROUSEL_SUBSTR));
}

function pickReferenceCard(list) {
  const listRect = list.getBoundingClientRect();
  const cards = [...list.querySelectorAll('product-card')].filter((c) => !c.hasAttribute('hidden'));
  if (!cards.length) {
    return null;
  }
  let best = cards[0];
  let bestOverlap = -1;
  for (const card of cards) {
    const cr = card.getBoundingClientRect();
    const overlap = Math.min(cr.right, listRect.right) - Math.max(cr.left, listRect.left);
    if (overlap > bestOverlap) {
      bestOverlap = overlap;
      best = card;
    }
  }
  return best;
}

function clearCarouselArrowVars(wrapper) {
  wrapper.style.removeProperty('--product-list-carousel-arrow-top');
  wrapper.style.removeProperty('--product-list-carousel-arrow-inset-start');
  wrapper.style.removeProperty('--product-list-carousel-arrow-inset-end');
}

function updateWrapper(wrapper) {
  const list = wrapper.querySelector('product-list');
  if (!listIsCarousel(list)) {
    clearCarouselArrowVars(wrapper);
    return;
  }

  const wr = wrapper.getBoundingClientRect();
  const lr = list.getBoundingClientRect();
  const rtl = getComputedStyle(wrapper).direction === 'rtl';

  /** 24px from list border to arrow (accounts for full-bleed vs wrapper) */
  const startInsetPx = rtl ? wr.right - lr.right + 24 : lr.left - wr.left + 24;
  const endInsetPx = rtl ? lr.left - wr.left + 24 : wr.right - lr.right + 24;

  if (Number.isFinite(startInsetPx) && Number.isFinite(endInsetPx)) {
    const r = (n) => `${Math.round(n * 10) / 10}px`;
    wrapper.style.setProperty('--product-list-carousel-arrow-inset-start', r(startInsetPx));
    wrapper.style.setProperty('--product-list-carousel-arrow-inset-end', r(endInsetPx));
  } else {
    wrapper.style.removeProperty('--product-list-carousel-arrow-inset-start');
    wrapper.style.removeProperty('--product-list-carousel-arrow-inset-end');
  }

  const card = pickReferenceCard(list);
  const figure = card?.querySelector('.product-card__figure');
  if (!figure) {
    wrapper.style.removeProperty('--product-list-carousel-arrow-top');
    return;
  }
  const fr = figure.getBoundingClientRect();
  if (!fr.height) {
    wrapper.style.removeProperty('--product-list-carousel-arrow-top');
    return;
  }
  const centerPx = fr.top - wr.top + fr.height / 2;
  if (!Number.isFinite(centerPx)) {
    return;
  }
  wrapper.style.setProperty('--product-list-carousel-arrow-top', `${Math.round(centerPx * 10) / 10}px`);
}

function bindWrapper(wrapper) {
  const list = wrapper.querySelector('product-list');

  if (!listIsCarousel(list)) {
    const prev = bindings.get(wrapper);
    if (prev) {
      prev.cleanup();
      bindings.delete(wrapper);
    }
    clearCarouselArrowVars(wrapper);
    return;
  }

  const existing = bindings.get(wrapper);
  if (existing && existing.list === list) {
    updateWrapper(wrapper);
    return;
  }
  if (existing) {
    existing.cleanup();
    bindings.delete(wrapper);
  }

  let scrollRaf = null;
  const onScrollOrResize = () => {
    if (scrollRaf) {
      cancelAnimationFrame(scrollRaf);
    }
    scrollRaf = requestAnimationFrame(() => {
      scrollRaf = null;
      updateWrapper(wrapper);
    });
  };

  const ro = new ResizeObserver(onScrollOrResize);
  ro.observe(list);
  ro.observe(wrapper);

  list.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize);

  const mo = new MutationObserver(onScrollOrResize);
  mo.observe(list, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'hidden', 'style'] });

  window.addEventListener('load', onScrollOrResize, { once: true });

  const cleanup = () => {
    if (scrollRaf) {
      cancelAnimationFrame(scrollRaf);
    }
    ro.disconnect();
    mo.disconnect();
    list.removeEventListener('scroll', onScrollOrResize);
    window.removeEventListener('resize', onScrollOrResize);
  };

  bindings.set(wrapper, { list, cleanup });
  updateWrapper(wrapper);
}

function scan() {
  document.querySelectorAll('.product-gallery__carousel-wrapper').forEach((wrapper) => {
    bindWrapper(wrapper);
  });
}

let rootMoScheduled = false;
function scheduleScan() {
  if (rootMoScheduled) {
    return;
  }
  rootMoScheduled = true;
  requestAnimationFrame(() => {
    rootMoScheduled = false;
    scan();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scan);
} else {
  scan();
}

new MutationObserver(scheduleScan).observe(document.documentElement, { childList: true, subtree: true });
